import React, { useRef, useState, useEffect, useMemo } from "react";
// RIMOSSO: import projectsData from "../data/projects.json";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, A11y } from "swiper/modules";
import { Link } from "react-router-dom";
import { MdArrowBackIosNew, MdArrowForwardIos } from "react-icons/md";
import { useTranslation } from "react-i18next";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

const Projects = () => {
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const [swiperInstance, setSwiperInstance] = useState(null);

  // Utilizziamo il namespace "projects"
  const { t } = useTranslation("projects");

  // Recuperiamo i dati tradotti invece del file statico
  const projectsData = useMemo(() => {
    const data = t("projects", { returnObjects: true });
    return Array.isArray(data) ? data : [];
  }, [t]);

  useEffect(() => {
    if (swiperInstance && swiperInstance.params?.navigation && prevRef.current && nextRef.current) {
      swiperInstance.params.navigation.prevEl = prevRef.current;
      swiperInstance.params.navigation.nextEl = nextRef.current;

      swiperInstance.navigation?.destroy?.();
      swiperInstance.navigation?.init?.();
      swiperInstance.navigation?.update?.();
    }
  }, [swiperInstance]);

  return (
    <section id="projects" className="relative overflow-hidden py-8">
      <div className="relative z-20 w-full max-w-6xl mx-auto flex flex-col justify-center gap-8 px-4">
        {/* Titolo */}
        <div className="text-center text-white">
          <h2
            className="text-2xl sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight
                       text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 
                       mt-4 sm:mt-16 md:mt-20 mb-8 leading-[1.2] py-[0.1em]"
            data-aos="fade-down"
          >
            {/* Usiamo la traduzione generale se il titolo è in it.json/en.json */}
            {t("translation:projects.title", "I Miei Progetti")}
          </h2>

          <div
            className="w-48 h-1 mx-auto mb-12 bg-gradient-to-r from-transparent via-purple-300 to-transparent"
            data-aos="fade-up"
          ></div>
        </div>

        {/* Swiper */}
        <div className="mt-4 mb-4 md:mb-16 relative">
          <Swiper
            onSwiper={setSwiperInstance}
            modules={[Navigation, Pagination, A11y]}
            spaceBetween={10}
            slidesPerView={1}
            centeredSlides={true}
            navigation={false}
            pagination={{ clickable: true }}
            breakpoints={{
              0: { slidesPerView: 1, centeredSlides: true, spaceBetween: 8 },
              375: { slidesPerView: 1, centeredSlides: true, spaceBetween: 8 },
              768: { slidesPerView: 2, centeredSlides: false, spaceBetween: 30 },
              1024: { slidesPerView: 3, centeredSlides: false, spaceBetween: 40 },
            }}
            className="custom-swiper-pagination pb-12"
          >
            {projectsData.map((project) => (
              <SwiperSlide key={project.id} className="flex justify-center">
                <ProjectCard project={project} />
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Frecce di navigazione */}
          <div
            ref={prevRef}
            className="hidden md:flex absolute top-1/2 -translate-y-1/2 left-[-30px] lg:left-[-50px]
                       z-30 cursor-pointer text-gray-400 hover:text-purple-400
                       p-2 transition-all duration-200 items-center justify-center 
                       w-12 h-12 focus:outline-none focus:ring-0"
          >
            <MdArrowBackIosNew className="text-3xl" />
          </div>

          <div
            ref={nextRef}
            className="hidden md:flex absolute top-1/2 -translate-y-1/2 right-[-30px] lg:right-[-50px]
                       z-30 cursor-pointer text-gray-400 hover:text-purple-400
                       p-2 transition-all duration-200 items-center justify-center 
                       w-12 h-12 focus:outline-none focus:ring-0"
          >
            <MdArrowForwardIos className="text-3xl" />
          </div>
        </div>
      </div>
    </section>
  );
};

const ProjectCard = ({ project }) => {
  // Specifichiamo i namespace se usiamo sia testi generali che quelli del progetto
  const { t } = useTranslation(["projects", "translation"]);

  return (
    <div
      className="border border-white/20 rounded-lg overflow-hidden 
                 w-full max-w-[90%] sm:max-w-[16rem] md:max-w-[19rem] mx-auto
                 h-[200px] sm:h-[220px] md:h-[240px]
                 relative group transition-all duration-300 mb-4 cursor-pointer
                 bg-white/10 backdrop-blur-md shadow-lg flex-shrink-0"
      data-aos="flip-left"
      data-aos-easing="ease-out-cubic"
      data-aos-duration="600"
    >
      <img src={project.image} alt={project.title} className="w-full h-28 sm:h-32 md:h-36 object-cover" />
      <div className="px-2 sm:px-3 mt-2 sm:mt-3">
        <h3 className="text-sm sm:text-base md:text-lg font-bold text-white text-left">{project.title}</h3>
      </div>

      <Link
        to={`/progetti/${project.id}`}
        className="absolute left-0 w-full h-36 sm:h-40 md:h-44 px-3 sm:px-4 py-3 sm:py-5 bg-black/90 backdrop-blur-lg
                   bottom-[-9rem] sm:bottom-[-10rem] md:bottom-[-11rem] 
                   group-hover:bottom-0 transition-all duration-500 ease-in-out"
      >
        <p className="text-gray-300 mb-3 text-xs sm:text-sm md:text-base leading-snug">{project.description}</p>
        <span className="inline-block text-white underline hover:text-purple-300 transition-colors duration-300 text-xs sm:text-sm md:text-base">
          {/* Cerchiamo la chiave seeMore nel namespace generale */}
          {t("translation:projects.seeMore", "Scopri di più")}
        </span>
      </Link>
    </div>
  );
};

export default Projects;
